<script context="module">
    // Cache em memória compartilhado em nível de módulo (persiste entre trocas de abas)
    let memoryTablesCache = [];
    let memoryProductsCache = [];
    let hasLoadedTablesOnce = false;
</script>

<script>
    import { onMount, onDestroy } from 'svelte';
    import { 
        fetchTables, 
        createTable, 
        deleteTable, 
        clearAllTables,
        openTable, 
        addTableItems, 
        removeTableItem, 
        updateTableItem,
        updateTableItemQty,
        getTableReceiptUrl,
        requestTableBill, 
        closeTable, 
        transferTable,
        recordPartialPayment,
        deletePartialPayment,
        updateTableItemsAssignment,
        fetchProducts,
        getApiBase,
        getCurrentStore
    } from '../services/api';
    import Icon from '../components/icons/Icon.svelte';
    import { categories, menuStore } from '../stores/menuStore';

    // Subcomponentes modulares de Mesas & Salão (Etapa 1.1, 1.2, 1.3 e 1.4)
    import TableCreateModal from '../components/tables/TableCreateModal.svelte';
    import TableTransferModal from '../components/tables/TableTransferModal.svelte';
    import TableDeleteModal from '../components/tables/TableDeleteModal.svelte';
    import ComandaModal from '../components/tables/ComandaModal.svelte';
    import ProductConfigModal from '../components/tables/ProductConfigModal.svelte';
    import CloseBillModal from '../components/tables/CloseBillModal.svelte';
    import SplitBillModal from '../components/tables/SplitBillModal.svelte';
    import TableOpenModal from '../components/tables/TableOpenModal.svelte';
    import TableGrid from '../components/tables/TableGrid.svelte';

    export let onNotify = (msg, type) => {};
    export let onPrintOrder = (order) => {};
    export let onPrintTableReceipt = ({ tableId, type, items }) => {};

    // Inicialização instantânea com dados em memória (0ms se já navegou anteriormente)
    let tables = memoryTablesCache.length > 0 ? [...memoryTablesCache] : [];
    let products = memoryProductsCache.length > 0 ? [...memoryProductsCache] : [];
    let isLoading = !hasLoadedTablesOnce && tables.length === 0;
    let hasLoadError = false;
    let loadErrorMessage = '';
    let filterStatus = 'all'; // 'all', 'available', 'occupied', 'bill_requested'
    let contingencyInterval = null;
    let lastFetchTimestamp = 0;
    let socket = null;
    let isLiveConnected = false;
    let isComponentMounted = true;

    // Estado da Comanda Selecionada / Drawer
    let selectedTable = null;
    let tableToDelete = null;
    let showComandaModal = false;
    let showOpenTableModal = false;
    let showCloseBillModal = false;
    let showNewTableModal = false;
    let showTransferModal = false;
    let showDeleteConfirmModal = false;
    let showClearAllConfirmModal = false;

    // Formulários
    let openCustomerName = '';
    let openPeopleCount = 2;
    let newTableNumber = '';
    let newTableLabel = '';
    let newTableSeats = 4;
    let transferTargetId = '';

    // Fechamento de Conta
    let closePaymentMethod = 'PIX';
    let closeDiscount = 0;
    let closeIncludeService = false;
    let isProcessingClose = false;

    // Divisão de Conta & Pagamentos Parciais
    let showSplitModal = false;
    let splitTab = 'equal'; // 'equal' | 'items'
    let splitPeopleCount = 2;
    let splitPersonName = 'Pessoa 1';
    let splitPaymentMethod = 'PIX';
    let splitCustomAmount = 0;
    let isProcessingSplitPayment = false;
    let splitCustomers = [];
    let newCustomerInput = '';
    let selectedCustomerForPayment = null;
    let openAssignDropdown = null;
    let showCustomPaymentForm = false;
    let selectedEqualShareIndex = 1;
    let showSplitHistory = false;

    // Métricas Reativas da Mesa e Divisão
    $: currentTableComanda = selectedTable?.comanda;
    $: currentItems = currentTableComanda?.items || [];
    $: currentItemsSum = currentItems.reduce((acc, it) => acc + (parseFloat(it.price || 0) * parseInt(it.qty || 1, 10)), 0);
    $: currentSubtotal = currentItemsSum > 0 ? parseFloat(currentItemsSum.toFixed(2)) : parseFloat(currentTableComanda?.subtotal || 0);
    $: currentServiceFee = closeIncludeService ? parseFloat((currentSubtotal * 0.10).toFixed(2)) : 0;
    $: currentDiscount = parseFloat(closeDiscount || 0);
    $: currentBillTotal = Math.max(0, parseFloat((currentSubtotal + currentServiceFee - currentDiscount).toFixed(2)));
    $: currentPayments = currentTableComanda?.payments || [];
    $: splitTotalPaid = parseFloat(currentPayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0).toFixed(2));
    $: splitRemaining = Math.max(0, parseFloat((currentBillTotal - splitTotalPaid).toFixed(2)));
    $: splitProgressPercent = currentBillTotal > 0 ? Math.min(100, Math.round((splitTotalPaid / currentBillTotal) * 100)) : 0;
    $: equalPerPerson = splitPeopleCount > 0 ? parseFloat((currentBillTotal / splitPeopleCount).toFixed(2)) : 0;

    $: customerTotalsMap = computeAllCustomerTotals(
        splitCustomers,
        currentItems,
        currentPayments,
        closeIncludeService,
        currentDiscount,
        currentBillTotal
    );

    // Adição de Itens & Lançamento Inteligente
    let showProductPicker = false;
    let productSearch = '';
    let configuringProduct = null;
    let configQty = 1;
    let configNotes = '';
    let configSelectedModifiers = {};
    let isSubmittingItem = false;
    let printDestination = 'kitchen'; // 'kitchen' | 'cashier' | 'both'
    let editingItem = null;
    let editingPendingIndex = -1;
    let isSavingEdit = false;
    let isPizzaHalfAndHalf = false;
    let selectedSecondProduct = null;

    // Lote de Pedido Pendente para Envio da Mesa Inteira
    let pendingOrderItems = [];
    let isSubmittingBatch = false;
    let currentStoreData = getCurrentStore();
    $: storeAllowsHalfBroto = Boolean(currentStoreData && (currentStoreData.allow_half_broto === 1 || currentStoreData.allow_half_broto === true || currentStoreData.allow_half_broto === "1"));
    $: pendingSubtotal = pendingOrderItems.reduce((acc, it) => acc + (parseFloat(it.price || 0) * parseInt(it.qty || 1, 10)), 0);
    $: productCategoryName = (
        configuringProduct?.category_name ||
        $categories?.find(c => c.id === configuringProduct?.category_id || c.id === configuringProduct?.categoryId)?.name ||
        ''
    ).toLowerCase();

    $: isDrink = (
        productCategoryName.includes('bebida') || 
        productCategoryName.includes('refrigerante') || 
        productCategoryName.includes('suco') || 
        productCategoryName.includes('cerveja') || 
        productCategoryName.includes('drink') ||
        productCategoryName.includes('água') ||
        productCategoryName.includes('agua') ||
        (configuringProduct?.name && /refrigerante|suco|cerveja|coca|guaraná|água|agua|heineken|chopp|chope|vinho/i.test(configuringProduct.name))
    );

    $: hasMeatDonenessInGroups = (() => {
        if (!configuringProduct) return false;
        const groups = configuringProduct.modifierGroups || configuringProduct.modifier_groups || [];
        return groups.some(g => {
            const gName = (g.name || '').toLowerCase();
            if (gName.includes('ponto') || gName.includes('carne') || gName.includes('cozimento')) return true;
            const opts = (g.options || []).map(o => (o.name || '').toLowerCase());
            return opts.some(o => o.includes('ponto') || o.includes('bem passado') || o.includes('mal passado'));
        });
    })();

    $: quickNoteChips = (() => {
        if (isDrink) {
            return [
                'Gelo e limão',
                'Sem gelo',
                'Com gelo',
                'Copo descartável',
                'Para viagem'
            ];
        }

        const chips = [];

        // Só inclui opções de ponto da carne se o produto for carne/hambúrguer e NÃO possuir grupo formal de ponto cadastrado
        if (!hasMeatDonenessInGroups) {
            const isMeatOrBurger = (
                productCategoryName.includes('burger') || 
                productCategoryName.includes('hambúrguer') ||
                productCategoryName.includes('hamburguer') ||
                productCategoryName.includes('carne') ||
                productCategoryName.includes('lanche') ||
                productCategoryName.includes('espeto') ||
                (configuringProduct?.name && /burger|hambúrguer|bife|carne|picanha|filé|costela/i.test(configuringProduct.name))
            );
            if (isMeatOrBurger) {
                chips.push('Ao ponto', 'Bem passado', 'Mal passado');
            }
        }

        chips.push(
            'Sem cebola',
            'Sem tomate',
            'Sem maionese',
            'Molho à parte',
            'Caprichado',
            'Para viagem'
        );

        return chips;
    })();

    function isSweetProduct(p) {
        if (!p) return false;
        const catName = p.category_name || $categories?.find(c => c.id === p.category_id || c.id === p.categoryId)?.name || '';
        const c = catName.toLowerCase();
        if (/\b(doces?|sobremesas?|chocolates?)\b/i.test(c)) {
            return true;
        }
        if (/\b(salgadas?|tradicionais?|especia(?:l|is))\b/i.test(c)) {
            const hasSavoryKeywords = /\b(lombo|bacon|calabresa|frango|carne|presunto|peito\s*de\s*peru|almondega|costela|palmito)\b/i.test(p.name || '');
            if (hasSavoryKeywords) {
                return false;
            }
        }
        const clean = (p.name || '')
            .replace(/^pizza\s+(?:de\s+|do\s+|da\s+)?/i, '')
            .replace(/\s*\([^)]*\)/g, '')
            .trim()
            .toLowerCase();
        const sweetRegex = /\b(banana|chocolate|morango|brigadeiro|romeu\s*e\s*julieta|beijinho|prestigio|prest[íi]gio|sensa[çc][aã]o|nutella|ninho|leite\s*ninho|churros|confete|m&ms?|mms|kit\s*kat|oreo|sonho\s*de\s*valsa|ouro\s*branco|kinder\s*bueno|doce\s*de\s*leite|abacaxi\s*com\s*canela|pa[çc]oca|fondue)\b/i;
        return sweetRegex.test(clean);
    }

    $: isConfiguringPizza = configuringProduct ? (
        (configuringProduct.category_name && configuringProduct.category_name.toLowerCase().includes('pizza')) ||
        (configuringProduct.name && configuringProduct.name.toLowerCase().includes('pizza')) ||
        ($categories?.find(c => c.id === configuringProduct.category_id || c.id === configuringProduct.categoryId)?.name?.toLowerCase().includes('pizza'))
    ) : false;

    $: isConfiguringSweet = configuringProduct ? isSweetProduct(configuringProduct) : false;

    // Regra Canônica: Não permite combinar sabores doces com salgados (Opção A: apenas sabores compatíveis)
    $: availableSecondFlavors = (isConfiguringPizza && products) ? products.filter(p => 
        p.id !== configuringProduct?.id && (
            (p.category_name && p.category_name.toLowerCase().includes('pizza')) ||
            (p.name && p.name.toLowerCase().includes('pizza')) ||
            ($categories?.find(c => c.id === p.category_id || c.id === p.categoryId)?.name?.toLowerCase().includes('pizza'))
        ) && p.active !== 0 && (isSweetProduct(p) === isConfiguringSweet)
    ) : [];

    function getProductEffectivePrice(prod) {
        if (!prod) return 0;
        const promo = parseFloat(prod.promo_price || prod.promoPrice || 0);
        const reg = parseFloat(prod.price || 0);
        return (promo > 0 && promo < reg) ? promo : reg;
    }

    function findMatchingSizePrice(secondProduct, sizeOpt) {
        if (!secondProduct || !sizeOpt) return 0;
        const groups = secondProduct.modifierGroups || secondProduct.modifier_groups || [];
        const sizeGroup = groups.find(g => /tamanho/i.test(g.name || ''));
        if (sizeGroup && Array.isArray(sizeGroup.options)) {
            const targetName = (sizeOpt.name || '').toLowerCase();
            const matchedOpt = sizeGroup.options.find(o => {
                if (o.id && sizeOpt.id && String(o.id) === String(sizeOpt.id)) return true;
                const oName = (o.name || '').toLowerCase();
                if (targetName.includes('broto') && oName.includes('broto')) return true;
                if (targetName.includes('grande') && oName.includes('grande')) return true;
                if (targetName.includes('pequena') && oName.includes('pequena')) return true;
                if (targetName.includes('média') && oName.includes('média')) return true;
                if (targetName.includes('media') && oName.includes('media')) return true;
                if (targetName.includes('gigante') && oName.includes('gigante')) return true;
                return false;
            }) || sizeGroup.options.find(o => (o.name || '').toLowerCase() === targetName);

            if (matchedOpt) {
                return parseFloat(matchedOpt.price || 0);
            }
        }
        return parseFloat(sizeOpt.price || 0);
    }

    $: configModifiersPrice = Object.values(configSelectedModifiers).flat().reduce((acc, opt) => acc + (parseFloat(opt?.price || 0)), 0);

    $: configUnitPrice = (() => {
        if (!configuringProduct) return 0;
        const p1Base = getProductEffectivePrice(configuringProduct);

        // Se for pizza meio a meio com 2º sabor selecionado:
        if (isPizzaHalfAndHalf && selectedSecondProduct) {
            const p2Base = getProductEffectivePrice(selectedSecondProduct);

            // Identifica se há modificador de tamanho selecionado no sabor 1
            const p1Groups = configuringProduct.modifierGroups || configuringProduct.modifier_groups || [];
            const sizeGroup = p1Groups.find(g => /tamanho/i.test(g.name || ''));
            const sizeGid = sizeGroup ? (sizeGroup.id || sizeGroup.name) : null;
            const sizeOpt = sizeGid ? (configSelectedModifiers[sizeGid] || [])[0] : null;

            const priceFlavor1 = p1Base + (sizeOpt ? parseFloat(sizeOpt.price || 0) : 0);
            const priceFlavor2 = p2Base + (sizeOpt ? findMatchingSizePrice(selectedSecondProduct, sizeOpt) : 0);
            const effectiveBasePrice = Math.max(priceFlavor1, priceFlavor2);

            // Modificadores que NÃO são de tamanho
            const nonSizeModifiersPrice = Object.entries(configSelectedModifiers).reduce((acc, [gid, opts]) => {
                if (gid === sizeGid) return acc;
                return acc + (opts || []).reduce((sub, opt) => sub + parseFloat(opt?.price || 0), 0);
            }, 0);

            return parseFloat((effectiveBasePrice + nonSizeModifiersPrice).toFixed(2));
        }

        // Caso normal (1 sabor ou produto comum)
        return parseFloat((p1Base + configModifiersPrice).toFixed(2));
    })();

    $: configBasePrice = isPizzaHalfAndHalf && selectedSecondProduct
        ? (() => {
            const p1Groups = configuringProduct?.modifierGroups || configuringProduct?.modifier_groups || [];
            const sizeGroup = p1Groups.find(g => /tamanho/i.test(g.name || ''));
            const sizeGid = sizeGroup ? (sizeGroup.id || sizeGroup.name) : null;
            const nonSizeModifiersPrice = Object.entries(configSelectedModifiers).reduce((acc, [gid, opts]) => {
                if (gid === sizeGid) return acc;
                return acc + (opts || []).reduce((sub, opt) => sub + parseFloat(opt?.price || 0), 0);
            }, 0);
            return parseFloat((configUnitPrice - nonSizeModifiersPrice).toFixed(2));
        })()
        : getProductEffectivePrice(configuringProduct);

    $: configTotalPrice = parseFloat((configUnitPrice * (configQty || 1)).toFixed(2));

    // Métricas Dinâmicas do Salão
    $: totalTables = tables.length;
    $: occupiedTables = tables.filter(t => t.status === 'occupied').length;
    $: billRequestedTables = tables.filter(t => t.status === 'bill_requested').length;
    $: availableTables = tables.filter(t => t.status === 'available').length;
    $: occupancyRate = totalTables > 0 ? Math.round(((occupiedTables + billRequestedTables) / totalTables) * 100) : 0;
    $: totalOpenRevenue = tables.reduce((sum, t) => sum + (t.comanda ? parseFloat(t.comanda.total || 0) : 0), 0);

    // Filtro das mesas
    $: filteredTables = tables.filter(t => {
        if (filterStatus === 'available') return t.status === 'available';
        if (filterStatus === 'occupied') return t.status === 'occupied';
        if (filterStatus === 'bill_requested') return t.status === 'bill_requested';
        return true;
    });

    // Filtro de produtos para adição
    $: filteredProducts = products.filter(p => {
        if (!productSearch) return true;
        const q = productSearch.toLowerCase();
        return p.name.toLowerCase().includes(q) || (p.description && p.description.toLowerCase().includes(q));
    });

    onMount(async () => {
        try {
            const savedDest = localStorage.getItem('matafome_table_print_dest');
            if (savedDest && ['kitchen', 'cashier', 'both'].includes(savedDest)) {
                printDestination = savedDest;
            }
        } catch (e) {}

        if (tables.length === 0) {
            await loadInitialData();
        } else {
            // Se já tínhamos mesas em cache na memória, revalida em segundo plano sem travar a UI
            loadTablesOnly();
            // E pré-carrega produtos em background se necessário
            if (products.length === 0) {
                fetchProducts().then(prods => {
                    if (Array.isArray(prods) && isComponentMounted) {
                        products = prods;
                        memoryProductsCache = prods;
                    }
                }).catch(() => {});
            }
            if ($categories.length === 0) {
                menuStore.syncFromApi().catch(() => {});
            }
        }

        if (typeof window !== 'undefined') {
            window.addEventListener('focus', onWindowFocus);
        }

        if (typeof window !== 'undefined' && window.io) {
            try {
                socket = window.io(getApiBase());
                socket.on("connect", () => {
                    isLiveConnected = true;
                    stopContingencyPolling();
                    const store = getCurrentStore();
                    const token = localStorage.getItem("matafome_token") || "";
                    if (store?.id) {
                        socket.emit("join_store", { 
                            storeId: store.id,
                            accessKey: store.access_key,
                            token: token
                        });
                    }
                    loadTablesOnly();
                });
                socket.on("disconnect", () => {
                    isLiveConnected = false;
                    startContingencyPolling(15000);
                });
                socket.on("connect_error", () => {
                    isLiveConnected = false;
                    startContingencyPolling(15000);
                });
                socket.on("table_updated", (payload) => {
                    handleTableUpdatedDelta(payload);
                });
                socket.on("kitchen_ticket_updated", (payload) => {
                    if (payload?.status === 'ready') {
                        const tableLabel = payload.tableLabel || (payload.tableNumber ? `Mesa ${payload.tableNumber}` : 'Mesa');
                        onNotify(`🔔 Pedido da ${tableLabel} está PRONTO na cozinha!`, 'success');
                        try {
                            const ctx = new (window.AudioContext || window.webkitAudioContext)();
                            const osc = ctx.createOscillator();
                            const gain = ctx.createGain();
                            osc.frequency.setValueAtTime(880, ctx.currentTime);
                            gain.gain.setValueAtTime(0.3, ctx.currentTime);
                            gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
                            osc.connect(gain);
                            gain.connect(ctx.destination);
                            osc.start();
                            osc.stop(ctx.currentTime + 0.4);
                            setTimeout(() => {
                                try {
                                    if (ctx && ctx.state !== 'closed') ctx.close();
                                } catch (_) {}
                            }, 600);
                        } catch (e) {}
                    }
                    loadTablesOnly();
                });
            } catch (e) {
                console.warn("[Tables] Falha ao conectar socket:", e);
                isLiveConnected = false;
                startContingencyPolling(15000);
            }
        } else {
            startContingencyPolling(15000);
        }
    });

    onDestroy(() => {
        isComponentMounted = false;
        stopContingencyPolling();
        if (socket) socket.disconnect();
        if (typeof window !== 'undefined') {
            window.removeEventListener('focus', onWindowFocus);
        }
    });

    async function loadInitialData() {
        if (!hasLoadedTablesOnce && tables.length === 0) {
            isLoading = true;
        }
        try {
            // 1. Dispara pré-carga de produtos em background se necessário (sem segurar o salão de mesas)
            if (products.length === 0) {
                fetchProducts().then((prods) => {
                    if (Array.isArray(prods) && isComponentMounted) {
                        products = prods;
                        memoryProductsCache = prods;
                    }
                }).catch((err) => {
                    console.warn('[Tables] Falha ao pré-carregar produtos do salão:', err);
                });
            }
            if ($categories.length === 0) {
                menuStore.syncFromApi().catch(() => {});
            }

            // 2. Busca mesas do salão (prioridade máxima para a tela de salão)
            const tabs = await fetchTables();
            if (isComponentMounted) {
                tables = tabs;
                memoryTablesCache = tabs;
                hasLoadedTablesOnce = true;
                hasLoadError = false;
                loadErrorMessage = '';
            }
        } catch (e) {
            console.error('Erro ao carregar mesas:', e);
            if (isComponentMounted) {
                if (tables.length === 0) {
                    hasLoadError = true;
                    loadErrorMessage = e.message || 'Não foi possível carregar as mesas do salão.';
                    onNotify('Não foi possível carregar as mesas do salão.', 'error');
                }
            }
        } finally {
            if (isComponentMounted) {
                isLoading = false;
            }
        }
    }

    function stopContingencyPolling() {
        if (contingencyInterval) {
            clearInterval(contingencyInterval);
            contingencyInterval = null;
        }
    }

    function startContingencyPolling(intervalMs = 15000) {
        stopContingencyPolling();
        contingencyInterval = setInterval(() => {
            if (!isLiveConnected && isComponentMounted) {
                loadTablesOnly();
            }
        }, intervalMs);
    }

    function onWindowFocus() {
        const now = Date.now();
        if (now - lastFetchTimestamp > 30000 && isComponentMounted) {
            loadTablesOnly();
        }
    }

    async function loadTablesOnly() {
        if (!isComponentMounted) return;
        lastFetchTimestamp = Date.now();
        try {
            const tabs = await fetchTables();
            if (!isComponentMounted) return;
            tables = tabs;
            memoryTablesCache = tabs;
            hasLoadedTablesOnce = true;
            hasLoadError = false;
            loadErrorMessage = '';
            // Se tiver uma mesa selecionada no modal, atualiza os dados dela
            if (selectedTable) {
                const updated = tables.find(t => t.id === selectedTable.id);
                if (updated) selectedTable = updated;
            }
        } catch (e) {
            if (!isComponentMounted) return;
            if (tables.length === 0) {
                hasLoadError = true;
                loadErrorMessage = e.message || 'Não foi possível carregar as mesas do salão.';
            }
        }
    }

    function handleTableUpdatedDelta(payload) {
        if (!payload || !payload.action) {
            loadTablesOnly();
            return;
        }

        const action = payload.action;
        const targetId = payload.tableId || payload.table?.id;

        if (action === 'created' && payload.table) {
            const exists = tables.some(t => t.id === payload.table.id);
            if (!exists) {
                tables = [...tables, payload.table];
            } else {
                tables = tables.map(t => t.id === payload.table.id ? payload.table : t);
            }
            memoryTablesCache = tables;
            return;
        }

        if (action === 'deleted' && targetId) {
            tables = tables.filter(t => t.id !== targetId);
            memoryTablesCache = tables;
            if (selectedTable && selectedTable.id === targetId) {
                selectedTable = null;
                showComandaModal = false;
            }
            return;
        }

        if (action === 'bulk_deleted' || action === 'transferred') {
            loadTablesOnly();
            return;
        }

        if (payload.table && payload.table.id) {
            const updatedTable = payload.table;
            const idx = tables.findIndex(t => t.id === updatedTable.id);
            if (idx !== -1) {
                tables[idx] = updatedTable;
                tables = [...tables];
                memoryTablesCache = tables;
                if (selectedTable && selectedTable.id === updatedTable.id) {
                    selectedTable = updatedTable;
                }
                return;
            }
        }

        loadTablesOnly();
    }

    function handleHeaderOpenTable() {
        const firstAvailable = tables.find(t => t.status === 'available');
        if (firstAvailable) {
            handleOpenTableClick(firstAvailable);
        } else {
            onNotify('Todas as mesas estão ocupadas. Cadastre uma nova mesa para abrir atendimento.', 'info');
            const nextNum = tables.length ? Math.max(...tables.map(t => t.table_number || 0)) + 1 : 1;
            newTableNumber = nextNum;
            newTableLabel = `Mesa ${String(nextNum).padStart(2, '0')}`;
            newTableSeats = 4;
            showNewTableModal = true;
        }
    }

    function handleOpenTableClick(table) {
        selectedTable = table;
        openCustomerName = '';
        openPeopleCount = table.seats || 2;
        showOpenTableModal = true;
    }

    async function handleConfirmOpenTable() {
        if (!selectedTable) return;
        try {
            const updated = await openTable(selectedTable.id, {
                customer_name: openCustomerName.trim() || `Cliente ${selectedTable.label}`,
                people_count: parseInt(openPeopleCount || 1, 10)
            });
            showOpenTableModal = false;
            selectedTable = updated;
            pendingOrderItems = [];
            showComandaModal = true;
            onNotify(`${selectedTable.label} aberta com sucesso!`, 'success');
            await loadTablesOnly();
        } catch (e) {
            onNotify(`Erro ao abrir mesa: ${e.message}`, 'error');
        }
    }

    function handleViewComanda(table) {
        if (selectedTable?.id !== table.id) {
            pendingOrderItems = [];
        }
        selectedTable = table;
        showComandaModal = true;
    }

    function openConfigureProduct(product) {
        if (!product) return;
        configuringProduct = product;
        configQty = 1;
        configNotes = '';
        configSelectedModifiers = {};
        isPizzaHalfAndHalf = false;
        selectedSecondProduct = null;
        
        const groups = (product.modifierGroups || product.modifier_groups || [])
            .filter(g => g && g.active !== 0 && g.active !== false);
        groups.forEach(g => {
            const gid = g.id || g.name;
            const activeOptions = (g.options || []).filter(o => o && o.active !== 0 && o.active !== false);
            const maxAllowed = g.max != null ? g.max : (g.max_choice != null ? g.max_choice : 1);
            if (g.required && maxAllowed === 1 && activeOptions.length > 0) {
                configSelectedModifiers[gid] = [activeOptions[0]];
            } else {
                configSelectedModifiers[gid] = [];
            }
        });
        configSelectedModifiers = { ...configSelectedModifiers };
    }

    function closeConfigureProduct() {
        configuringProduct = null;
        editingItem = null;
        editingPendingIndex = -1;
        configQty = 1;
        configNotes = '';
        configSelectedModifiers = {};
        isPizzaHalfAndHalf = false;
        selectedSecondProduct = null;
    }

    function toggleModifierOption(group, option) {
        const gid = group.id || group.name;
        const current = configSelectedModifiers[gid] || [];
        const existsIndex = current.findIndex(o => ((o.id && option.id && String(o.id) === String(option.id)) || (o.name && option.name && o.name.trim().toLowerCase() === option.name.trim().toLowerCase())));

        const maxAllowed = group.max != null ? group.max : (group.max_choice != null ? group.max_choice : 1);
        if (maxAllowed === 1) {
            if (existsIndex >= 0) {
                if (!group.required) {
                    configSelectedModifiers[gid] = [];
                }
            } else {
                configSelectedModifiers[gid] = [option];
            }
        } else {
            if (existsIndex >= 0) {
                configSelectedModifiers[gid] = current.filter((_, i) => i !== existsIndex);
            } else {
                if (current.length < maxAllowed) {
                    configSelectedModifiers[gid] = [...current, option];
                } else {
                    onNotify(`Máximo de ${maxAllowed} opções para "${group.name}".`, 'warning');
                }
            }
        }
        configSelectedModifiers = { ...configSelectedModifiers };
    }

    function isOptionSelected(groupOrMap, optionOrGroup, opt) {
        let selectedMap = configSelectedModifiers;
        let group = groupOrMap;
        let option = optionOrGroup;
        if (opt !== undefined) {
            selectedMap = groupOrMap;
            group = optionOrGroup;
            option = opt;
        }
        if (!selectedMap || !group || !option) return false;
        const gid = group.id || group.name;
        const current = selectedMap[gid] || [];
        return current.some(o => ((o.id && option.id && String(o.id) === String(option.id)) || (o.name && option.name && o.name.trim().toLowerCase() === option.name.trim().toLowerCase())));
    }

    function toggleQuickNote(chip) {
        const current = configNotes.trim();
        if (!current) {
            configNotes = chip;
            return;
        }
        const parts = current.split(',').map(s => s.trim()).filter(Boolean);
        const idx = parts.indexOf(chip);
        if (idx >= 0) {
            parts.splice(idx, 1);
            configNotes = parts.join(', ');
        } else {
            parts.push(chip);
            configNotes = parts.join(', ');
        }
    }

    function validateModifiers() {
        if (!configuringProduct) return false;
        const groups = (configuringProduct.modifierGroups || configuringProduct.modifier_groups || [])
            .filter(g => g && g.active !== 0 && g.active !== false);
        for (const g of groups) {
            const gid = g.id || g.name;
            const selected = (configSelectedModifiers[gid] || []).filter(o => o && o.active !== 0 && o.active !== false);
            const min = g.min !== undefined ? g.min : (g.required ? 1 : 0);
            if (g.required && selected.length < min) {
                onNotify(`Selecione ao menos ${min} opção em "${g.name}".`, 'warning');
                return false;
            }
        }
        return true;
    }

    function setPrintDestination(dest) {
        printDestination = dest;
        try {
            localStorage.setItem('matafome_table_print_dest', dest);
        } catch (e) {}
    }

    function getDisplayNotes(notes, modifiers) {
        if (!notes || typeof notes !== 'string') return '';
        let clean = notes.trim();
        if (!clean) return '';

        // Se contém o separador legado '// Obs:', extrai apenas a observação livre real
        const obsIndex = clean.indexOf('// Obs:');
        if (obsIndex !== -1) {
            return clean.substring(obsIndex + 7).trim();
        }

        // Se o item já possui modificadores estruturados e a nota começa com prefixos de serialização legada (+ ou •)
        if (modifiers && Array.isArray(modifiers) && modifiers.length > 0) {
            if (clean.startsWith('+ ') || clean.startsWith('• ')) {
                const modNames = modifiers.map(m => (typeof m === 'object' && m ? m.name : String(m)).trim().toLowerCase());
                const parts = clean.split(/\s*\|\s*/).map(p => p.replace(/^[+•]\s*/, '').replace(/\s*\(R\$.*?\)/, '').trim().toLowerCase());
                const allMatch = parts.every(p => modNames.includes(p));
                if (allMatch) {
                    return '';
                }
            }
        }

        return clean;
    }

    function buildConfiguredItem() {
        if (!configuringProduct) return null;
        if (!validateModifiers()) return null;

        if (isPizzaHalfAndHalf && !selectedSecondProduct) {
            onNotify('Por favor, selecione o 2º sabor da pizza meio a meio.', 'warning');
            return null;
        }

        if (isPizzaHalfAndHalf && selectedSecondProduct) {
            if (isSweetProduct(configuringProduct) !== isSweetProduct(selectedSecondProduct)) {
                onNotify('Não é permitido combinar sabores doces e salgados na mesma pizza.', 'warning');
                return null;
            }
            const sizeGroup = (configuringProduct?.modifierGroups || configuringProduct?.modifier_groups || []).find(g => /tamanho/i.test(g.name || ''));
            const selectedSize = sizeGroup ? (configSelectedModifiers[sizeGroup.id || sizeGroup.name] || [])[0] : null;
            if (selectedSize && /\b(broto|pequena|individual|mini|\bp\b)\b/i.test(selectedSize.name || '')) {
                if (!storeAllowsHalfBroto) {
                    onNotify('O tamanho Broto ou Pequena não permite divisão em 2 sabores (meio a meio).', 'warning');
                    return null;
                }
            }
        }

        const selectedOpts = Object.values(configSelectedModifiers).flat()
            .filter(o => o && o.active !== 0 && o.active !== false);
        const formattedModifiers = selectedOpts.map(o => ({
            id: o.id,
            name: o.name,
            price: parseFloat(o.price || 0)
        }));

        let cleanFreeNotes = configNotes ? configNotes.trim() : '';

        let itemName = configuringProduct.name;
        let fullName = configuringProduct.name;
        let flavor1Name = null;
        let flavor2Name = null;

        if (isPizzaHalfAndHalf && selectedSecondProduct) {
            const clean1 = configuringProduct.name.replace(/^pizza\s+/i, '').trim();
            const clean2 = selectedSecondProduct.name.replace(/^pizza\s+/i, '').trim();
            const fullHalfName = `Pizza Meio a Meio (1/2 ${clean1} + 1/2 ${clean2})`;
            itemName = fullHalfName;
            flavor1Name = clean1;
            flavor2Name = clean2;
            fullName = fullHalfName;
        }

        return {
            id: (editingItem && editingItem.id) ? editingItem.id : `draft-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
            productId: configuringProduct.id,
            secondProductId: isPizzaHalfAndHalf && selectedSecondProduct ? selectedSecondProduct.id : null,
            isHalfAndHalf: isPizzaHalfAndHalf && !!selectedSecondProduct,
            name: itemName,
            flavor1Name,
            flavor2Name,
            fullName,
            price: parseFloat(configUnitPrice.toFixed(2)),
            qty: parseInt(configQty || 1, 10),
            notes: cleanFreeNotes,
            modifiers: formattedModifiers
        };
    }

    // 1. Adiciona ao rascunho de pedidos da mesa (0ms, permitindo lançar a mesa inteira antes de enviar)
    function handleAddItemToOrderDraft() {
        if (!selectedTable) {
            onNotify('Nenhuma mesa selecionada.', 'warning');
            return;
        }
        const item = buildConfiguredItem();
        if (!item) return;

        pendingOrderItems = [...pendingOrderItems, item];
        const addedName = item.name;
        const addedQty = item.qty;
        closeConfigureProduct();
        onNotify(`${addedQty}x "${addedName}" adicionado ao pedido! Adicione outros itens ou envie para a cozinha/caixa.`, 'success');
    }

    function updatePendingItemQty(index, delta) {
        if (!pendingOrderItems[index]) return;
        const newQty = (pendingOrderItems[index].qty || 1) + delta;
        if (newQty <= 0) {
            removePendingItem(index);
        } else {
            pendingOrderItems[index].qty = newQty;
            pendingOrderItems = [...pendingOrderItems];
        }
    }

    function removePendingItem(index) {
        pendingOrderItems = pendingOrderItems.filter((_, i) => i !== index);
    }

    function clearPendingOrder() {
        pendingOrderItems = [];
    }

    // 2. Envio consolidado do lote para a mesa (e impressão térmica única)
    async function handleSubmitBatchOrder(shouldPrint = true) {
        if (!selectedTable) {
            onNotify('Selecione uma mesa.', 'warning');
            return;
        }
        if (pendingOrderItems.length === 0) {
            onNotify('Nenhum item pendente para enviar.', 'warning');
            return;
        }

        isSubmittingBatch = true;
        const itemsToSend = [...pendingOrderItems];
        try {
            const updated = await addTableItems(selectedTable.id, itemsToSend, closeIncludeService);
            if (updated) {
                selectedTable = updated.table || updated;
            }

            pendingOrderItems = [];
            onNotify(`${itemsToSend.length} produto(s) lançado(s) com sucesso na ${selectedTable.label}!`, 'success');

            if (shouldPrint && onPrintTableReceipt) {
                onPrintTableReceipt({
                    tableId: selectedTable.id,
                    type: printDestination || 'kitchen',
                    items: itemsToSend
                });
            }

            await loadTablesOnly();
        } catch (e) {
            onNotify(`Erro ao enviar pedido para a mesa: ${e.message}`, 'error');
        } finally {
            isSubmittingBatch = false;
        }
    }

    // Lançamento rápido direto do modal do produto (junta pendentes se houver)
    async function handleConfirmAddItem(shouldPrint = true) {
        if (!selectedTable) return;
        const item = buildConfiguredItem();
        if (!item) return;

        const allItems = [...pendingOrderItems, item];
        isSubmittingItem = true;
        try {
            const updated = await addTableItems(selectedTable.id, allItems, closeIncludeService);
            if (updated) {
                selectedTable = updated.table || updated;
            }
            pendingOrderItems = [];
            closeConfigureProduct();
            showProductPicker = false;

            onNotify(`${allItems.length} produto(s) lançado(s) na ${selectedTable.label}!`, 'success');

            if (shouldPrint && onPrintTableReceipt) {
                onPrintTableReceipt({
                    tableId: selectedTable.id,
                    type: printDestination || 'kitchen',
                    items: allItems
                });
            }

            await loadTablesOnly();
        } catch (e) {
            onNotify(`Erro ao lançar item: ${e.message}`, 'error');
        } finally {
            isSubmittingItem = false;
        }
    }

    let quickAddingProductIds = new Set();

    function isSimpleProduct(prod) {
        if (!prod) return false;
        const groups = prod.modifierGroups || prod.modifier_groups;
        // Não classificar como simples se a lista de complementos não foi carregada (undefined)
        if (!Array.isArray(groups)) return false;
        if (groups.length > 0) return false;
        // Se for pizza, exige configuração para sabores / meio a meio
        const isPizza = (prod.category_name && prod.category_name.toLowerCase().includes('pizza')) ||
            (prod.name && prod.name.toLowerCase().includes('pizza')) ||
            ($categories?.find(c => c.id === prod.category_id || c.id === prod.categoryId)?.name?.toLowerCase().includes('pizza'));
        if (isPizza) return false;
        return true;
    }

    async function handleQuickAddProduct(product) {
        if (!selectedTable) {
            onNotify('Selecione ou abra uma mesa antes de lançar produtos.', 'warning');
            return;
        }
        if (quickAddingProductIds.has(product.id)) {
            return; // Previne cliques repetidos acidentais
        }
        quickAddingProductIds.add(product.id);
        quickAddingProductIds = new Set(quickAddingProductIds);

        try {
            const promo = parseFloat(product.promo_price || product.promoPrice || 0);
            const reg = parseFloat(product.price || 0);
            const unitPrice = (promo > 0 && promo < reg) ? promo : reg;

            const simpleItem = {
                id: `quick-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
                productId: product.id,
                name: product.name,
                price: parseFloat(unitPrice.toFixed(2)),
                qty: 1,
                notes: '',
                modifiers: []
            };

            const updated = await addTableItems(selectedTable.id, [simpleItem], closeIncludeService);
            if (updated) {
                selectedTable = updated.table || updated;
            }
            onNotify(`1x ${product.name} adicionado(a) à comanda!`, 'success');

            if (onPrintTableReceipt) {
                onPrintTableReceipt({
                    tableId: selectedTable.id,
                    type: printDestination || 'kitchen',
                    items: [simpleItem]
                });
            }

            await loadTablesOnly();
        } catch (e) {
            onNotify(`Erro ao adicionar ${product.name}: ${e.message}`, 'error');
        } finally {
            setTimeout(() => {
                quickAddingProductIds.delete(product.id);
                quickAddingProductIds = new Set(quickAddingProductIds);
            }, 350);
        }
    }

    async function handleAddItem(product) {
        if (isSimpleProduct(product)) {
            await handleQuickAddProduct(product);
        } else {
            openConfigureProduct(product);
        }
    }

    // 3. Stepper de quantidade já lançada: 100% otimista (0ms), silencioso e sem popup invasivo
    async function handleUpdateItemQty(index, delta) {
        if (!selectedTable || !selectedTable.comanda) return;
        const targetItem = selectedTable.comanda.items?.[index];
        if (!targetItem) return;

        const currentQty = parseInt(targetItem.qty || 1, 10);
        const newQty = currentQty + delta;

        if (newQty <= 0) {
            handleRemoveItem(index);
            return;
        }

        // Atualização otimista imediata na interface (0ms de latência)
        const previousQty = targetItem.qty;
        targetItem.qty = newQty;
        selectedTable = { ...selectedTable };

        try {
            const res = await updateTableItemQty(selectedTable.id, index, { delta });
            if (res) {
                selectedTable = res.table || res;
            }
        } catch (e) {
            // Reverte em caso de erro na conexão
            targetItem.qty = previousQty;
            selectedTable = { ...selectedTable };
            onNotify(`Erro ao sincronizar quantidade: ${e.message}`, 'error');
        }
    }

    async function handleRemoveItem(index) {
        if (!selectedTable || !selectedTable.comanda) return;
        if (!confirm('Deseja estornar este item da comanda?')) return;
        try {
            const updated = await removeTableItem(selectedTable.id, index);
            selectedTable = updated;
            onNotify('Item estornado com sucesso.', 'info');
            await loadTablesOnly();
        } catch (e) {
            onNotify(`Erro ao remover: ${e.message}`, 'error');
        }
    }

    function handleOpenEditItem(item) {
        if (!item) return;
        if (item.kitchenStatus === 'ready') {
            onNotify('Este item já foi marcado como pronto pela cozinha e não pode ser alterado diretamente.', 'warning');
            return;
        }
        if (item.kitchenStatus === 'cancelled') {
            onNotify('Este item foi cancelado e não pode ser editado.', 'warning');
            return;
        }

        const prod = products.find(p => p.id === item.productId) || {
            id: item.productId,
            name: item.name,
            price: item.basePrice !== undefined ? item.basePrice : item.price,
            modifier_groups: []
        };

        editingItem = item;
        editingPendingIndex = -1;
        configuringProduct = prod;
        configQty = item.qty || 1;
        configNotes = getDisplayNotes(item.notes, item.modifiers);
        configSelectedModifiers = {};

        if (item.isHalfAndHalf && item.secondProductId) {
            isPizzaHalfAndHalf = true;
            selectedSecondProduct = products.find(p => p.id === item.secondProductId) || {
                id: item.secondProductId,
                name: item.flavor2Name || 'Segundo Sabor',
                price: item.price
            };
        } else {
            isPizzaHalfAndHalf = false;
            selectedSecondProduct = null;
        }

        const groups = (prod.modifierGroups || prod.modifier_groups || [])
            .filter(g => g && g.active !== 0 && g.active !== false);
        const savedModifiers = item.modifiers || [];

        groups.forEach(g => {
            const gid = g.id || g.name;
            const matchedOpts = (g.options || []).filter(opt =>
                savedModifiers.some(m => {
                    if (!m) return false;
                    if (typeof m === 'object') {
                        if (m.id && opt.id && String(m.id) === String(opt.id)) return true;
                        if (m.name && opt.name && m.name.trim().toLowerCase() === opt.name.trim().toLowerCase()) return true;
                        return false;
                    }
                    return String(m).trim().toLowerCase() === String(opt.name || '').trim().toLowerCase();
                })
            );
            configSelectedModifiers[gid] = matchedOpts;
        });
    }

    function handleEditPendingItem(pItem, pIdx) {
        if (!pItem) return;
        const prod = products.find(p => p.id === pItem.productId) || {
            id: pItem.productId,
            name: pItem.name,
            price: pItem.basePrice !== undefined ? pItem.basePrice : pItem.price,
            modifier_groups: []
        };

        editingItem = null;
        editingPendingIndex = pIdx;
        configuringProduct = prod;
        configQty = pItem.qty || 1;
        configNotes = getDisplayNotes(pItem.notes, pItem.modifiers);
        configSelectedModifiers = {};

        if (pItem.isHalfAndHalf && pItem.secondProductId) {
            isPizzaHalfAndHalf = true;
            selectedSecondProduct = products.find(p => p.id === pItem.secondProductId) || {
                id: pItem.secondProductId,
                name: pItem.flavor2Name || 'Segundo Sabor',
                price: pItem.price
            };
        } else {
            isPizzaHalfAndHalf = false;
            selectedSecondProduct = null;
        }

        const groups = (prod.modifierGroups || prod.modifier_groups || [])
            .filter(g => g && g.active !== 0 && g.active !== false);
        const savedModifiers = pItem.modifiers || [];

        groups.forEach(g => {
            const gid = g.id || g.name;
            const matchedOpts = (g.options || []).filter(opt =>
                savedModifiers.some(m => {
                    if (!m) return false;
                    if (typeof m === 'object') {
                        if (m.id && opt.id && String(m.id) === String(opt.id)) return true;
                        if (m.name && opt.name && m.name.trim().toLowerCase() === opt.name.trim().toLowerCase()) return true;
                        return false;
                    }
                    return String(m).trim().toLowerCase() === String(opt.name || '').trim().toLowerCase();
                })
            );
            configSelectedModifiers[gid] = matchedOpts;
        });
    }

    async function handleSaveEditedItem() {
        if (!configuringProduct) return;
        if (!validateModifiers()) return;

        if (isPizzaHalfAndHalf && !selectedSecondProduct) {
            onNotify('Por favor, selecione o 2º sabor da pizza meio a meio.', 'warning');
            return;
        }

        if (isPizzaHalfAndHalf && selectedSecondProduct) {
            if (isSweetProduct(configuringProduct) !== isSweetProduct(selectedSecondProduct)) {
                onNotify('Não é permitido combinar sabores doces e salgados na mesma pizza.', 'warning');
                return;
            }
            const sizeGroup = (configuringProduct?.modifierGroups || configuringProduct?.modifier_groups || []).find(g => /tamanho/i.test(g.name || ''));
            const selectedSize = sizeGroup ? (configSelectedModifiers[sizeGroup.id || sizeGroup.name] || [])[0] : null;
            if (selectedSize && /\b(broto|pequena|individual|mini|\bp\b)\b/i.test(selectedSize.name || '')) {
                if (!storeAllowsHalfBroto) {
                    onNotify('O tamanho Broto ou Pequena não permite divisão em 2 sabores (meio a meio).', 'warning');
                    return;
                }
            }
        }

        // 1. Se estiver editando item do lote pendente (rascunho pré-envio)
        if (editingPendingIndex >= 0 && pendingOrderItems[editingPendingIndex]) {
            const item = buildConfiguredItem();
            if (!item) return;
            pendingOrderItems[editingPendingIndex] = {
                ...pendingOrderItems[editingPendingIndex],
                ...item
            };
            pendingOrderItems = [...pendingOrderItems];
            closeConfigureProduct();
            onNotify('Item do rascunho atualizado com sucesso.', 'info');
            return;
        }

        // 2. Se estiver editando item já lançado na mesa (PUT no backend)
        if (!selectedTable || !editingItem) return;
        isSavingEdit = true;
        try {
            const selectedOpts = Object.values(configSelectedModifiers).flat();
            const formattedModifiers = selectedOpts.map(o => ({
                id: o.id,
                name: o.name,
                price: parseFloat(o.price || 0)
            }));

            const res = await updateTableItem(selectedTable.id, editingItem.id, {
                productId: configuringProduct.id,
                secondProductId: isPizzaHalfAndHalf && selectedSecondProduct ? selectedSecondProduct.id : null,
                isHalfAndHalf: isPizzaHalfAndHalf && !!selectedSecondProduct,
                qty: configQty,
                notes: configNotes ? configNotes.trim() : '',
                modifiers: formattedModifiers
            });

            if (res && res.table) {
                selectedTable = res.table;
            }
            const wasInPrep = editingItem.batchId || editingItem.kitchenStatus === 'in_prep';
            const editedItemId = editingItem.id;
            const changeId = res?.changeId || null;
            closeConfigureProduct();

            if (wasInPrep) {
                onNotify('Item atualizado com sucesso! Imprimindo retificação para a cozinha...', 'success');
                if (onPrintTableReceipt) {
                    onPrintTableReceipt({
                        tableId: selectedTable.id,
                        type: 'rectification',
                        itemId: editedItemId,
                        changeId
                    });
                }
            } else {
                onNotify('Item atualizado com sucesso!', 'success');
            }
            await loadTablesOnly();
        } catch (e) {
            onNotify(`Erro ao atualizar item: ${e.message}`, 'error');
        } finally {
            isSavingEdit = false;
        }
    }

    function handlePrintItemRectification(item) {
        if (!selectedTable || !item) return;
        const changeId = item.lastChange?.changeId || (Array.isArray(item.productionChanges) && item.productionChanges.length > 0 ? item.productionChanges[item.productionChanges.length - 1].changeId : null);
        if (onPrintTableReceipt) {
            onPrintTableReceipt({
                tableId: selectedTable.id,
                type: 'rectification',
                itemId: item.id,
                changeId
            });
        }
    }

    async function handlePrintPreBill() {
        if (!selectedTable || !selectedTable.comanda) return;
        try {
            await requestTableBill(selectedTable.id, {
                serviceFeeIncluded: closeIncludeService,
                discount: closeDiscount
            });
            await loadTablesOnly();

            if (onPrintTableReceipt) {
                onPrintTableReceipt({
                    tableId: selectedTable.id,
                    type: 'pre_bill'
                });
            } else if (onPrintOrder) {
                const comanda = selectedTable.comanda;
                const mockOrder = {
                    id: `MESA-${selectedTable.table_number}`,
                    order_number: `M${selectedTable.table_number}`,
                    client: `${comanda.customer_name || 'Cliente'} (${selectedTable.label})`,
                    total: parseFloat(comanda.total || 0),
                    payment: "Conferência de Mesa (Pré-Conta)"
                };
                onPrintOrder(mockOrder);
            }
            onNotify(`Pré-conta da ${selectedTable.label} enviada para impressão!`, 'success');
        } catch (e) {
            onNotify(`Erro ao imprimir pré-conta: ${e.message}`, 'error');
        }
    }

    function handleOpenCloseModal() {
        if (!selectedTable) return;
        showComandaModal = false;
        showCloseBillModal = true;
    }

    async function handleConfirmCloseBill() {
        if (!selectedTable) return;
        isProcessingClose = true;
        try {
            const res = await closeTable(selectedTable.id, {
                payment_method: closePaymentMethod,
                discount: parseFloat(closeDiscount || 0),
                serviceFeeIncluded: closeIncludeService
            });
            showCloseBillModal = false;
            selectedTable = null;
            onNotify(res.message || 'Conta fechada e mesa liberada!', 'success');
            await loadTablesOnly();
        } catch (e) {
            onNotify(`Erro ao fechar conta: ${e.message}`, 'error');
        } finally {
            isProcessingClose = false;
        }
    }

    async function handleConfirmTransfer() {
        if (!selectedTable || !transferTargetId) return;
        try {
            await transferTable(selectedTable.id, transferTargetId);
            showTransferModal = false;
            showComandaModal = false;
            selectedTable = null;
            onNotify('Mesa transferida com sucesso!', 'success');
            await loadTablesOnly();
        } catch (e) {
            onNotify(`Erro ao transferir: ${e.message}`, 'error');
        }
    }

    function handleOpenSplitModal() {
        if (!selectedTable || !selectedTable.comanda) return;
        splitPeopleCount = Math.max(2, selectedTable.comanda.people_count || 2);
        
        // Carrega clientes da comanda ou inicializa
        if (selectedTable.comanda.split_customers && selectedTable.comanda.split_customers.length > 0) {
            splitCustomers = [...selectedTable.comanda.split_customers];
        } else {
            splitCustomers = [];
            for (let i = 1; i <= splitPeopleCount; i++) {
                splitCustomers.push(`Pessoa ${i}`);
            }
        }

        selectedCustomerForPayment = null;
        selectedEqualShareIndex = 1;
        showSplitHistory = false;
        const paidCount = (selectedTable.comanda.payments || []).length;
        splitPersonName = `Pessoa ${paidCount + 1}`;
        const perPerson = parseFloat((currentBillTotal / splitPeopleCount).toFixed(2));
        splitCustomAmount = splitRemaining < perPerson ? splitRemaining : perPerson;

        showComandaModal = false;
        showSplitModal = true;
    }

    function addSplitCustomer() {
        const name = newCustomerInput.trim();
        if (!name) return;
        if (splitCustomers.some(c => c.trim().toLowerCase() === name.toLowerCase())) {
            onNotify('Já existe um cliente com este nome.', 'warning');
            return;
        }
        splitCustomers = [...splitCustomers, name];
        splitPeopleCount = splitCustomers.length;
        newCustomerInput = '';
        if (selectedTable && selectedTable.comanda) {
            updateTableItemsAssignment(selectedTable.id, {
                items: [],
                split_customers: splitCustomers
            }).catch(() => {});
        }
    }

    function removeSplitCustomer(name) {
        splitCustomers = splitCustomers.filter(c => c !== name);
        if (splitPeopleCount > splitCustomers.length) {
            splitPeopleCount = Math.max(1, splitCustomers.length);
        }
        if (selectedCustomerForPayment === name) {
            selectedCustomerForPayment = null;
        }
        if (selectedTable && selectedTable.comanda) {
            const modifiedItems = [];
            const items = (selectedTable.comanda.items || []).map(it => {
                if (Array.isArray(it.assigned_to) && it.assigned_to.includes(name)) {
                    const nextAssigned = it.assigned_to.filter(c => c !== name);
                    const updated = { ...it, assigned_to: nextAssigned, assignedTo: nextAssigned };
                    modifiedItems.push(updated);
                    return updated;
                }
                return it;
            });
            selectedTable = {
                ...selectedTable,
                comanda: {
                    ...selectedTable.comanda,
                    items
                }
            };
            updateTableItemsAssignment(selectedTable.id, {
                items: modifiedItems,
                split_customers: splitCustomers,
                removed_customer: name
            }).catch(() => {});
        }
    }

    function toggleItemAssignment(itemIndex, customer) {
        if (!selectedTable || !selectedTable.comanda) return;
        const currentItemsList = selectedTable.comanda.items || [];
        let toggledItem = null;
        const items = currentItemsList.map((it, idx) => {
            if (idx !== itemIndex) return it;
            const updated = { ...it };
            if (customer === 'all') {
                updated.assigned_to = [];
                updated.assignedTo = [];
            } else {
                let currentAssigned = Array.isArray(updated.assigned_to) ? [...updated.assigned_to] : [];
                if (currentAssigned.includes(customer)) {
                    currentAssigned = currentAssigned.filter(c => c !== customer);
                } else {
                    currentAssigned.push(customer);
                }
                updated.assigned_to = currentAssigned;
                updated.assignedTo = currentAssigned;
            }
            toggledItem = updated;
            return updated;
        });

        // Reatribuição imutável para disparar a reatividade imediata no Svelte
        selectedTable = {
            ...selectedTable,
            comanda: {
                ...selectedTable.comanda,
                items
            }
        };

        // Se tiver cliente selecionado no formulário de pagamento de itens, atualiza o valor
        if (selectedCustomerForPayment) {
            const nextTotals = computeAllCustomerTotals(
                splitCustomers,
                items,
                currentPayments,
                closeIncludeService,
                closeDiscount,
                currentBillTotal
            );
            if (nextTotals[selectedCustomerForPayment]) {
                splitCustomAmount = nextTotals[selectedCustomerForPayment].remaining;
            }
        }

        if (toggledItem) {
            updateTableItemsAssignment(selectedTable.id, {
                items: [toggledItem],
                split_customers: splitCustomers
            }).catch(e => console.warn('Erro ao salvar atribuição:', e));
        }
    }

    function calculateRawCustomerTotal(customer, customers, items, payments, includeService, discount, billTotal) {
        if (!customer) {
            return { subtotal: 0, serviceFee: 0, discount: 0, total: 0, paid: 0, remaining: 0, isPaid: false, items: [] };
        }
        const numCustomers = Math.max(1, (customers || []).length);
        let custSubtotal = 0;
        let custItems = [];

        (items || []).forEach(it => {
            const itemTotal = parseFloat(it.price || 0) * parseInt(it.qty || 1, 10);
            const assigned = Array.isArray(it.assigned_to) ? it.assigned_to : [];
            if (assigned.length === 0) {
                // Compartilhado igualmente entre todos os clientes
                const share = itemTotal / numCustomers;
                custSubtotal += share;
                custItems.push({ name: it.name, qty: it.qty, share, isShared: true });
            } else if (assigned.includes(customer)) {
                // Compartilhado apenas entre os selecionados
                const share = itemTotal / assigned.length;
                custSubtotal += share;
                custItems.push({ name: it.name, qty: it.qty, share, isShared: assigned.length > 1 });
            }
        });

        const custServiceFee = includeService ? parseFloat((custSubtotal * 0.10).toFixed(2)) : 0;
        const totalSubtotal = (items || []).reduce((sum, it) => sum + (parseFloat(it.price || 0) * parseInt(it.qty || 1, 10)), 0);
        const custDiscount = (totalSubtotal > 0 && discount > 0)
            ? parseFloat(((custSubtotal / totalSubtotal) * discount).toFixed(2))
            : 0;

        const custTotal = Math.max(0, parseFloat((custSubtotal + custServiceFee - custDiscount).toFixed(2)));

        const custPaid = (payments || [])
            .filter(p => p.person_name && p.person_name.trim().toLowerCase() === customer.trim().toLowerCase())
            .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

        const tableTotalPaid = (payments || []).reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
        const tableRemaining = Math.max(0, parseFloat((billTotal - tableTotalPaid).toFixed(2)));

        const custRemaining = Math.min(tableRemaining, Math.max(0, parseFloat((custTotal - custPaid).toFixed(2))));
        const isPaid = (custPaid > 0 && custRemaining <= 0.009) || (custTotal === 0 && (payments || []).length > 0);

        return {
            subtotal: parseFloat(custSubtotal.toFixed(2)),
            serviceFee: custServiceFee,
            discount: custDiscount,
            total: custTotal,
            paid: parseFloat(custPaid.toFixed(2)),
            remaining: custRemaining,
            isPaid,
            items: custItems
        };
    }

    function computeAllCustomerTotals(customers, items, payments, includeService, discount, billTotal) {
        if (!customers || customers.length === 0) return {};
        const map = {};
        let sumTotals = 0;

        customers.forEach(c => {
            const raw = calculateRawCustomerTotal(c, customers, items, payments, includeService, discount, billTotal);
            map[c] = raw;
            sumTotals += raw.total;
        });

        // Ajuste fino de arredondamento de centavos caso a soma divirja em centavos do total da mesa
        const roundingDiff = parseFloat((billTotal - sumTotals).toFixed(2));
        if (Math.abs(roundingDiff) > 0 && Math.abs(roundingDiff) < 0.20 && customers.length > 0) {
            const lastCust = customers[customers.length - 1];
            if (map[lastCust]) {
                map[lastCust].total = Math.max(0, parseFloat((map[lastCust].total + roundingDiff).toFixed(2)));
                const tableTotalPaid = (payments || []).reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
                const tableRemaining = Math.max(0, parseFloat((billTotal - tableTotalPaid).toFixed(2)));
                map[lastCust].remaining = Math.min(tableRemaining, Math.max(0, parseFloat((map[lastCust].total - map[lastCust].paid).toFixed(2))));
                map[lastCust].isPaid = (map[lastCust].paid > 0 && map[lastCust].remaining <= 0.009) || (map[lastCust].total === 0 && (payments || []).length > 0);
            }
        }

        // Se houver apenas 1 cliente pendente com saldo, ajusta para o saldo restante exato da mesa
        const tableTotalPaid = (payments || []).reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
        const tableRemaining = Math.max(0, parseFloat((billTotal - tableTotalPaid).toFixed(2)));
        const unpaidList = customers.filter(c => map[c] && map[c].remaining > 0);
        if (unpaidList.length === 1 && tableRemaining > 0) {
            const solePayer = unpaidList[0];
            map[solePayer].remaining = tableRemaining;
        }

        return map;
    }

    function calculateCustomerTotal(customer) {
        if (customerTotalsMap && customerTotalsMap[customer]) {
            return customerTotalsMap[customer];
        }
        return calculateRawCustomerTotal(
            customer,
            splitCustomers,
            selectedTable?.comanda?.items || [],
            currentPayments,
            closeIncludeService,
            closeDiscount,
            currentBillTotal
        );
    }

    function preparePayForCustomer(customer) {
        const custCalc = customerTotalsMap?.[customer] || calculateCustomerTotal(customer);
        if (custCalc.isPaid || custCalc.remaining <= 0) {
            onNotify(`A cota de ${customer} já está quitada!`, 'info');
            return;
        }
        selectedCustomerForPayment = customer;
        splitPersonName = customer;
        splitCustomAmount = custCalc.remaining;
        splitPaymentMethod = 'PIX';
        // Não muda splitTab! Continua na aba de itens
        onNotify(`Recebimento de ${customer} (R$ ${formatCurrency(splitCustomAmount)}) selecionado.`, 'info');
    }

    async function quickPayEqualShare(personName, method, amount) {
        if (!selectedTable || !selectedTable.comanda) return;
        const amountToPay = Math.min(splitRemaining, parseFloat(amount || equalPerPerson || 0));
        if (amountToPay <= 0) return;

        isProcessingSplitPayment = true;
        try {
            const res = await recordPartialPayment(selectedTable.id, {
                amount: amountToPay,
                payment_method: method,
                person_name: personName,
                serviceFeeIncluded: closeIncludeService,
                discount: parseFloat(closeDiscount || 0)
            });

            if (res.tableClosed) {
                showSplitModal = false;
                selectedTable = null;
                selectedCustomerForPayment = null;
                onNotify(res.message || 'Conta quitada integralmente! Mesa liberada.', 'success');
                await loadTablesOnly();
            } else {
                onNotify(`Pagamento de ${personName} (${method}) recebido com sucesso!`, 'success');
                selectedTable = res.table;
                await loadTablesOnly();
            }
        } catch (e) {
            onNotify(`Erro ao registrar pagamento: ${e.message}`, 'error');
        } finally {
            isProcessingSplitPayment = false;
        }
    }

    async function quickPayCustomer(customer, method, amount) {
        if (!selectedTable || !selectedTable.comanda) return;
        const custCalc = customerTotalsMap?.[customer] || calculateCustomerTotal(customer);
        const amountToPay = Math.min(splitRemaining, parseFloat(amount || custCalc.remaining || 0));
        if (amountToPay <= 0) return;

        isProcessingSplitPayment = true;
        try {
            const res = await recordPartialPayment(selectedTable.id, {
                amount: amountToPay,
                payment_method: method,
                person_name: customer,
                serviceFeeIncluded: closeIncludeService,
                discount: parseFloat(closeDiscount || 0)
            });

            if (res.tableClosed) {
                showSplitModal = false;
                selectedTable = null;
                selectedCustomerForPayment = null;
                onNotify(res.message || 'Conta quitada integralmente! Mesa liberada.', 'success');
                await loadTablesOnly();
            } else {
                onNotify(`Pagamento de ${customer} (${method}) recebido com sucesso!`, 'success');
                selectedTable = res.table;
                await loadTablesOnly();
            }
        } catch (e) {
            onNotify(`Erro ao registrar pagamento: ${e.message}`, 'error');
        } finally {
            isProcessingSplitPayment = false;
        }
    }

    async function handleRecordSplitPayment() {
        if (!selectedTable || !selectedTable.comanda) return;
        const amountToPay = parseFloat(splitCustomAmount || 0);
        if (amountToPay <= 0) {
            onNotify('Informe um valor de pagamento maior que R$ 0,00.', 'warning');
            return;
        }
        if (amountToPay > splitRemaining + 0.01) {
            onNotify(`O valor informado (R$ ${formatCurrency(amountToPay)}) excede o saldo restante da mesa (R$ ${formatCurrency(splitRemaining)}).`, 'warning');
            return;
        }

        isProcessingSplitPayment = true;
        try {
            const payerName = (splitPersonName || (splitTab === 'items' ? selectedCustomerForPayment : '') || `Pessoa ${(selectedTable.comanda.payments || []).length + 1}`).trim();
            const res = await recordPartialPayment(selectedTable.id, {
                amount: amountToPay,
                payment_method: splitPaymentMethod,
                person_name: payerName,
                serviceFeeIncluded: closeIncludeService,
                discount: parseFloat(closeDiscount || 0)
            });

            if (res.tableClosed) {
                showSplitModal = false;
                selectedTable = null;
                selectedCustomerForPayment = null;
                onNotify(res.message || 'Conta quitada integralmente! Mesa liberada.', 'success');
                await loadTablesOnly();
            } else {
                onNotify(res.message || 'Pagamento parcial registrado!', 'success');
                selectedTable = res.table;
                const newRemaining = parseFloat((res.remaining !== undefined ? res.remaining : Math.max(0, currentBillTotal - (res.totalPaid || 0))).toFixed(2));

                if (splitTab === 'items') {
                    // Atualiza cálculos com os novos pagamentos
                    const newTotals = computeAllCustomerTotals(
                        splitCustomers,
                        selectedTable?.comanda?.items || [],
                        selectedTable?.comanda?.payments || [],
                        closeIncludeService,
                        closeDiscount,
                        currentBillTotal
                    );
                    // Procura o próximo cliente pendente
                    const nextUnpaid = splitCustomers.find(c => (newTotals[c]?.remaining || 0) > 0);
                    if (nextUnpaid) {
                        selectedCustomerForPayment = nextUnpaid;
                        splitPersonName = nextUnpaid;
                        splitCustomAmount = Math.min(newRemaining, newTotals[nextUnpaid]?.remaining || newRemaining);
                    } else {
                        selectedCustomerForPayment = null;
                        splitCustomAmount = newRemaining;
                    }
                } else {
                    const paidCount = (selectedTable.comanda?.payments || []).length;
                    splitPersonName = `Pessoa ${paidCount + 1}`;
                    const perPerson = splitPeopleCount > 0 ? parseFloat((currentBillTotal / splitPeopleCount).toFixed(2)) : newRemaining;
                    splitCustomAmount = Math.min(newRemaining, perPerson);
                }
                await loadTablesOnly();
            }
        } catch (e) {
            onNotify(`Erro ao registrar pagamento: ${e.message}`, 'error');
        } finally {
            isProcessingSplitPayment = false;
        }
    }

    async function handleDeleteSplitPayment(paymentId) {
        if (!selectedTable) return;
        if (!confirm('Deseja estornar este pagamento parcial?')) return;
        try {
            const res = await deletePartialPayment(selectedTable.id, paymentId);
            selectedTable = res.table;
            onNotify('Pagamento parcial estornado com sucesso.', 'info');
            await loadTablesOnly();
        } catch (e) {
            onNotify(`Erro ao estornar: ${e.message}`, 'error');
        }
    }

    async function handleCreateNewTable() {
        if (!newTableNumber) {
            onNotify('Informe o número da mesa.', 'error');
            return;
        }
        try {
            await createTable({
                table_number: parseInt(newTableNumber, 10),
                label: newTableLabel.trim() || `Mesa ${String(newTableNumber).padStart(2, '0')}`,
                seats: parseInt(newTableSeats || 4, 10)
            });
            showNewTableModal = false;
            newTableNumber = '';
            newTableLabel = '';
            onNotify('Nova mesa adicionada com sucesso!', 'success');
            await loadTablesOnly();
        } catch (e) {
            onNotify(`Erro: ${e.message}`, 'error');
        }
    }

    function promptDeleteTable(table, event) {
        if (event) event.stopPropagation();
        if (!table) return;
        if (table.status !== 'available') {
            onNotify('Não é possível excluir uma mesa em atendimento ou com conta pedida.', 'warning');
            return;
        }
        tableToDelete = table;
        showDeleteConfirmModal = true;
    }

    async function executeDeleteTable() {
        if (!tableToDelete) return;
        const targetId = tableToDelete.id;
        const targetLabel = tableToDelete.label;
        showDeleteConfirmModal = false;

        // Atualização Otimista Imediata na Interface (0ms)
        tables = tables.filter(t => t.id !== targetId);
        memoryTablesCache = tables;

        try {
            await deleteTable(targetId);
            onNotify(`${targetLabel} excluída com sucesso!`, 'info');
            await loadTablesOnly();
        } catch (e) {
            onNotify(`Erro ao excluir mesa: ${e.message}`, 'error');
            await loadTablesOnly();
        } finally {
            tableToDelete = null;
        }
    }

    function promptClearAllTables() {
        if (availableTables === 0) {
            onNotify('Não há mesas livres para excluir.', 'info');
            return;
        }
        showClearAllConfirmModal = true;
    }

    async function executeClearAllTables() {
        showClearAllConfirmModal = false;

        // Atualização Otimista Imediata na Interface (0ms)
        tables = tables.filter(t => t.status !== 'available');
        memoryTablesCache = tables;

        try {
            await clearAllTables();
            onNotify('Todas as mesas livres foram excluídas com sucesso!', 'success');
            await loadTablesOnly();
        } catch (e) {
            onNotify(`Erro ao limpar mesas: ${e.message}`, 'error');
            await loadTablesOnly();
        }
    }

    function formatCurrency(val) {
        const num = parseFloat(val);
        return isNaN(num) ? '0,00' : num.toFixed(2).replace('.', ',');
    }

    function getTimeDiff(dateStr) {
        if (!dateStr) return '0 min';
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return '0 min';
        const mins = Math.max(1, Math.round((Date.now() - d.getTime()) / 60000));
        if (mins >= 60) {
            const hrs = Math.floor(mins / 60);
            const m = mins % 60;
            return `${hrs}h ${m}m`;
        }
        return `${mins} min`;
    }
</script>

<div class="tables-view">
    <!-- Header com Resumo do Salão & Ações -->
    <header class="tables-header">
        <div class="header-titles">
            <div class="header-badge-row">
                <div class="header-badge">
                    <Icon name="utensils" size={13} />
                    <span>Salão & Atendimento Presencial</span>
                </div>
                <div class="live-sync-indicator" class:connected={isLiveConnected} title={isLiveConnected ? "Sincronização em tempo real ativa via WebSocket" : "Sincronização periódica ativa"}>
                    <span class="live-dot"></span>
                    <span>{isLiveConnected ? 'Tempo Real' : 'Sincronizado'}</span>
                </div>
            </div>
            <h1>Mesas & Salão</h1>
            <p class="subtitle">
                Gestão operacional de mesas, comandas em tempo real e fechamento de contas.
            </p>
        </div>

        <div class="header-main-actions">
            <button class="btn-primary btn-header-open" on:click={handleHeaderOpenTable}>
                <Icon name="plus" size={16} strokeWidth={2.5} />
                <span>Abrir Mesa</span>
            </button>
        </div>
    </header>

    <!-- Faixa Operacional Compacta (KPIs do Salão) -->
    <section class="kpi-bar" aria-label="Resumo Operacional do Salão">
        <div class="kpi-item">
            <span class="kpi-label">Total</span>
            <span class="kpi-value">{totalTables}</span>
            <span class="kpi-sub">mesas cadastradas</span>
        </div>

        <div class="kpi-divider"></div>

        <div class="kpi-item">
            <span class="kpi-label">Livres</span>
            <div class="kpi-val-row">
                <span class="kpi-status-dot green"></span>
                <span class="kpi-value text-success">{availableTables}</span>
            </div>
            <span class="kpi-sub">prontas p/ abrir</span>
        </div>

        <div class="kpi-divider"></div>

        <div class="kpi-item">
            <span class="kpi-label">Ocupadas</span>
            <div class="kpi-val-row">
                <span class="kpi-status-dot blue"></span>
                <span class="kpi-value text-info">{occupiedTables}</span>
            </div>
            <span class="kpi-sub">em atendimento</span>
        </div>

        <div class="kpi-divider"></div>

        <div class="kpi-item" class:has-alert={billRequestedTables > 0}>
            <span class="kpi-label">Aguardando Conta</span>
            <div class="kpi-val-row">
                <span class="kpi-status-dot orange"></span>
                <span class="kpi-value {billRequestedTables > 0 ? 'text-warning' : ''}">{billRequestedTables}</span>
            </div>
            <span class="kpi-sub">contas pedidas</span>
        </div>

        <div class="kpi-divider"></div>

        <div class="kpi-item kpi-occupancy">
            <div class="kpi-label-row">
                <span class="kpi-label">Ocupação</span>
                <span class="kpi-pct-badge {occupancyRate > 80 ? 'high' : ''}">{occupancyRate}%</span>
            </div>
            <div class="kpi-progress-track" title="{occupiedTables + billRequestedTables} de {totalTables} mesas ocupadas">
                <div class="kpi-progress-bar {occupancyRate > 80 ? 'high' : ''}" style="width: {Math.min(100, occupancyRate)}%"></div>
            </div>
            <span class="kpi-sub">{occupiedTables + billRequestedTables} de {totalTables} mesas</span>
        </div>

        <div class="kpi-divider"></div>

        <div class="kpi-item kpi-revenue">
            <span class="kpi-label">Consumo em Aberto</span>
            <span class="kpi-value kpi-revenue-val">R$ {formatCurrency(totalOpenRevenue)}</span>
            <span class="kpi-sub">faturamento no salão</span>
        </div>
    </section>

    <!-- Barra de Filtros e Ações -->
    <div class="tables-toolbar">
        <div class="filter-pills" role="tablist" aria-label="Filtrar mesas">
            <button
                class="filter-pill"
                class:active={filterStatus === 'all'}
                on:click={() => filterStatus = 'all'}
                role="tab"
                aria-selected={filterStatus === 'all'}
            >
                Todas <span class="pill-count">{totalTables}</span>
            </button>
            <button
                class="filter-pill"
                class:active={filterStatus === 'available'}
                on:click={() => filterStatus = 'available'}
                role="tab"
                aria-selected={filterStatus === 'available'}
            >
                <span class="dot-status green"></span>
                Livres <span class="pill-count">{availableTables}</span>
            </button>
            <button
                class="filter-pill"
                class:active={filterStatus === 'occupied'}
                on:click={() => filterStatus = 'occupied'}
                role="tab"
                aria-selected={filterStatus === 'occupied'}
            >
                <span class="dot-status blue"></span>
                Ocupadas <span class="pill-count">{occupiedTables}</span>
            </button>
            <button
                class="filter-pill"
                class:active={filterStatus === 'bill_requested'}
                on:click={() => filterStatus = 'bill_requested'}
                role="tab"
                aria-selected={filterStatus === 'bill_requested'}
            >
                <span class="dot-status orange"></span>
                Conta Solicitada <span class="pill-count">{billRequestedTables}</span>
            </button>
        </div>

        <div class="toolbar-actions">
            {#if availableTables > 0}
                <button class="btn-outline-danger" on:click={promptClearAllTables} title="Excluir todas as mesas livres para recadastrar">
                    <Icon name="trash" size={14} />
                    <span>Limpar Mesas Livres</span>
                </button>
            {/if}

            <button class="btn-secondary-action" on:click={() => {
                const nextNum = tables.length ? Math.max(...tables.map(t => t.table_number || 0)) + 1 : 1;
                newTableNumber = nextNum;
                newTableLabel = `Mesa ${String(nextNum).padStart(2, '0')}`;
                newTableSeats = 4;
                showNewTableModal = true;
            }}>
                <Icon name="plus" size={14} />
                <span>Nova Mesa</span>
            </button>
        </div>
    </div>

    <!-- Grid de Mesas (Modularizado Sub-etapa 1.4) -->
    <TableGrid
        {isLoading}
        {hasLoadError}
        {loadErrorMessage}
        {filterStatus}
        {filteredTables}
        on:retryLoad={loadInitialData}
        on:createFirstTable={() => { newTableNumber = 1; newTableLabel = 'Mesa 01'; newTableSeats = 4; showNewTableModal = true; }}
        on:resetFilter={() => filterStatus = 'all'}
        on:openTable={(e) => handleOpenTableClick(e.detail)}
        on:deleteTable={(e) => promptDeleteTable(e.detail.table, e.detail.event)}
        on:viewComanda={(e) => handleViewComanda(e.detail)}
        on:closeTable={(e) => { selectedTable = e.detail; handleOpenCloseModal(); }}
    />
</div>

<!-- MODAL: ABRIR MESA (Modularizado Sub-etapa 1.4) -->
<TableOpenModal
    show={showOpenTableModal}
    bind:selectedTable
    {tables}
    bind:customerName={openCustomerName}
    bind:peopleCount={openPeopleCount}
    on:close={() => showOpenTableModal = false}
    on:confirm={handleConfirmOpenTable}
/>

<!-- MODAL / DRAWER: COMANDA DA MESA (Modularizado Sub-etapa 1.2) -->
<ComandaModal
    show={showComandaModal}
    {selectedTable}
    {products}
    categories={$categories}
    {pendingOrderItems}
    {pendingSubtotal}
    {printDestination}
    {isSubmittingBatch}
    {quickAddingProductIds}
    bind:closeIncludeService
    {closeDiscount}
    {splitTotalPaid}
    {splitRemaining}
    {splitProgressPercent}
    on:close={() => showComandaModal = false}
    on:quickAddProduct={(e) => handleQuickAddProduct(e.detail)}
    on:configureProduct={(e) => openConfigureProduct(e.detail)}
    on:updatePendingItemQty={(e) => updatePendingItemQty(e.detail.index, e.detail.delta)}
    on:editPendingItem={(e) => handleEditPendingItem(e.detail.item, e.detail.index)}
    on:removePendingItem={(e) => removePendingItem(e.detail)}
    on:clearPendingOrder={clearPendingOrder}
    on:setPrintDestination={(e) => setPrintDestination(e.detail)}
    on:submitBatchOrder={(e) => handleSubmitBatchOrder(e.detail.shouldPrint)}
    on:updateItemQty={(e) => handleUpdateItemQty(e.detail.index, e.detail.delta)}
    on:printRectification={(e) => handlePrintItemRectification(e.detail)}
    on:editItem={(e) => handleOpenEditItem(e.detail)}
    on:removeItem={(e) => handleRemoveItem(e.detail)}
    on:openSplitModal={handleOpenSplitModal}
    on:printPreBill={handlePrintPreBill}
    on:printReceipt={(e) => onPrintTableReceipt({ tableId: selectedTable.id, type: e.detail.type })}
    on:openTransferModal={() => { showTransferModal = true; }}
    on:openCloseModal={handleOpenCloseModal}
/>

<!-- MODAL: CONFIGURADOR / LANÇAMENTO INTELIGENTE DE ITEM (Modularizado Sub-etapa 1.2) -->
<ProductConfigModal
    allowHalfBroto={storeAllowsHalfBroto}
    {configuringProduct}
    {editingItem}
    {editingPendingIndex}
    {isConfiguringPizza}
    bind:isPizzaHalfAndHalf
    bind:selectedSecondProduct
    {availableSecondFlavors}
    bind:configQty
    {configUnitPrice}
    {configTotalPrice}
    bind:configNotes
    {configSelectedModifiers}
    {quickNoteChips}
    {hasMeatDonenessInGroups}
    {printDestination}
    {isSavingEdit}
    {isSubmittingItem}
    on:close={closeConfigureProduct}
    on:toggleModifier={(e) => toggleModifierOption(e.detail.group, e.detail.opt)}
    on:toggleQuickNote={(e) => toggleQuickNote(e.detail.chip)}
    on:setPrintDestination={(e) => setPrintDestination(e.detail.dest)}
    on:saveEdit={handleSaveEditedItem}
    on:addToDraft={handleAddItemToOrderDraft}
    on:confirmAdd={(e) => handleConfirmAddItem(e.detail.shouldPrint)}
/>

<!-- MODAL: FECHAR CONTA DA MESA (Modularizado Sub-etapa 1.3) -->
<CloseBillModal
    show={showCloseBillModal}
    {selectedTable}
    bind:closePaymentMethod
    bind:closeDiscount
    bind:closeIncludeService
    bind:splitPeopleCount
    {currentSubtotal}
    {currentServiceFee}
    {currentBillTotal}
    {currentPayments}
    {splitTotalPaid}
    {splitRemaining}
    {equalPerPerson}
    {isProcessingClose}
    on:close={() => { showCloseBillModal = false; }}
    on:printPreBill={handlePrintPreBill}
    on:openSplit={() => { showCloseBillModal = false; handleOpenSplitModal(); }}
    on:confirmClose={handleConfirmCloseBill}
/>

<!-- MODAL: DIVIDIR CONTA / PAGAMENTO PARCIAL (Modularizado Sub-etapa 1.3) -->
<SplitBillModal
    show={showSplitModal}
    {selectedTable}
    bind:closeIncludeService
    bind:closeDiscount
    bind:splitPeopleCount
    bind:splitCustomers
    {currentSubtotal}
    {currentServiceFee}
    {currentBillTotal}
    {currentPayments}
    {splitTotalPaid}
    {splitRemaining}
    {splitProgressPercent}
    {equalPerPerson}
    {customerTotalsMap}
    {isProcessingSplitPayment}
    on:close={() => { showSplitModal = false; showComandaModal = true; }}
    on:quickPayEqualShare={(e) => quickPayEqualShare(e.detail.personName, e.detail.method, e.detail.amount)}
    on:quickPayCustomer={(e) => quickPayCustomer(e.detail.customer, e.detail.method, e.detail.amount)}
    on:recordSplitPayment={(e) => {
        splitCustomAmount = e.detail.amount;
        splitPaymentMethod = e.detail.paymentMethod;
        splitPersonName = e.detail.personName;
        handleRecordSplitPayment();
    }}
    on:addCustomer={(e) => {
        newCustomerInput = e.detail.name;
        addSplitCustomer();
    }}
    on:removeCustomer={(e) => removeSplitCustomer(e.detail.name)}
    on:toggleItemAssignment={(e) => toggleItemAssignment(e.detail.itemIndex, e.detail.customer)}
    on:deletePayment={(e) => handleDeleteSplitPayment(e.detail.paymentId)}
    on:confirmCloseBill={() => { showSplitModal = false; handleConfirmCloseBill(); }}
/>

<!-- MODAL: TRANSFERIR MESA (Modularizado) -->
<TableTransferModal
    show={showTransferModal}
    {selectedTable}
    {tables}
    bind:transferTargetId
    on:close={() => showTransferModal = false}
    on:submit={handleConfirmTransfer}
/>

<!-- MODAL: CADASTRAR NOVA MESA (Modularizado) -->
<TableCreateModal
    show={showNewTableModal}
    bind:newTableNumber
    bind:newTableLabel
    bind:newTableSeats
    on:close={() => showNewTableModal = false}
    on:submit={handleCreateNewTable}
/>

<!-- MODAL: CONFIRMAR EXCLUSÃO DE MESA (Modularizado) -->
<TableDeleteModal
    show={showDeleteConfirmModal}
    table={tableToDelete}
    isClearAll={false}
    on:close={() => showDeleteConfirmModal = false}
    on:confirm={executeDeleteTable}
/>

<!-- MODAL: CONFIRMAR LIMPAR TODAS AS MESAS LIVRES (Modularizado) -->
<TableDeleteModal
    show={showClearAllConfirmModal}
    isClearAll={true}
    availableCount={availableTables}
    on:close={() => showClearAllConfirmModal = false}
    on:confirm={executeClearAllTables}
/>


<style>
    .tables-view {
        padding: 24px 32px;
        height: 100%;
        height: calc(100vh - 36px);
        height: calc(100dvh - 36px);
        box-sizing: border-box;
        overflow-y: auto;
        background: var(--neutral-bg, #F8FAFC);
        color: var(--text-primary, #0F172A);
        font-family: inherit;
    }

    /* HEADER */
    .tables-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 20px;
        margin-bottom: 20px;
        padding-bottom: 18px;
        border-bottom: 1px solid var(--neutral-border, #E2E8F0);
    }

    .header-titles {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    .header-badge-row {
        display: flex;
        align-items: center;
        gap: 10px;
        flex-wrap: wrap;
    }

    .header-badge {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        background: var(--brand-primary-soft, #FFF7ED);
        color: var(--brand-primary, #FF6A00);
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        padding: 4px 10px;
        border-radius: var(--radius-full, 9999px);
        border: 1px solid rgba(255, 106, 0, 0.2);
    }

    .live-sync-indicator {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-size: 11px;
        font-weight: 600;
        color: var(--text-muted, #94A3B8);
        padding: 3px 10px;
        border-radius: var(--radius-full, 9999px);
        background: #FFFFFF;
        border: 1px solid var(--neutral-border, #E2E8F0);
        transition: all 0.2s ease;
    }

    .live-sync-indicator.connected {
        color: var(--semantic-success, #059669);
        border-color: rgba(5, 150, 105, 0.25);
        background: var(--semantic-success-soft, #ECFDF5);
    }

    .live-dot {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: #94A3B8;
        transition: all 0.2s ease;
    }

    .live-sync-indicator.connected .live-dot {
        background: var(--semantic-success, #059669);
        box-shadow: 0 0 0 2px rgba(5, 150, 105, 0.2);
        animation: livePulse 2s infinite ease-in-out;
    }

    @keyframes livePulse {
        0%, 100% { transform: scale(1); opacity: 1; }
        50% { transform: scale(1.25); opacity: 0.7; }
    }

    h1 {
        font-size: 24px;
        font-weight: 800;
        margin: 4px 0 2px 0;
        color: var(--text-primary, #0F172A);
        letter-spacing: -0.5px;
    }

    .subtitle {
        color: var(--text-secondary, #64748B);
        font-size: 13.5px;
        margin: 0;
    }

    .header-main-actions {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .btn-header-open {
        box-shadow: 0 2px 8px rgba(255, 106, 0, 0.28);
        padding: 9px 18px;
        font-size: 13.5px;
    }

    /* KPI BAR */
    .kpi-bar {
        display: flex;
        align-items: center;
        background: #FFFFFF;
        border: 1px solid var(--neutral-border, #E2E8F0);
        border-radius: var(--radius-lg, 16px);
        padding: 12px 20px;
        gap: 16px;
        margin-bottom: 20px;
        box-shadow: var(--shadow-sm, 0 1px 2px rgba(15, 23, 42, 0.05));
    }

    .kpi-item {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
    }

    .kpi-label {
        font-size: 10.5px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: var(--text-muted, #64748B);
    }

    .kpi-val-row {
        display: flex;
        align-items: center;
        gap: 6px;
    }

    .kpi-value {
        font-size: 17px;
        font-weight: 800;
        color: var(--text-primary, #0F172A);
        line-height: 1.2;
    }

    .kpi-sub {
        font-size: 10.5px;
        color: var(--text-secondary, #64748B);
        white-space: nowrap;
    }

    .kpi-status-dot {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        flex-shrink: 0;
    }

    .kpi-status-dot.green { background: var(--semantic-success, #059669); }
    .kpi-status-dot.blue { background: var(--semantic-info, #2563EB); }
    .kpi-status-dot.orange { background: var(--semantic-warning, #D97706); }

    .kpi-divider {
        width: 1px;
        height: 36px;
        background: var(--neutral-border, #E2E8F0);
        flex-shrink: 0;
    }

    .kpi-occupancy {
        flex: 1.2;
        min-width: 130px;
    }

    .kpi-label-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .kpi-pct-badge {
        font-size: 10.5px;
        font-weight: 800;
        color: var(--brand-primary, #FF6A00);
        background: var(--brand-primary-soft, #FFF7ED);
        padding: 1px 6px;
        border-radius: 9999px;
    }

    .kpi-pct-badge.high {
        color: var(--semantic-warning, #D97706);
        background: var(--semantic-warning-soft, #FFFBEB);
    }

    .kpi-progress-track {
        width: 100%;
        height: 6px;
        background: #F1F5F9;
        border-radius: 9999px;
        overflow: hidden;
        margin: 4px 0 2px 0;
    }

    .kpi-progress-bar {
        height: 100%;
        background: var(--brand-primary, #FF6A00);
        border-radius: 9999px;
        transition: width 0.4s ease;
    }

    .kpi-progress-bar.high {
        background: var(--semantic-warning, #D97706);
    }

    .kpi-revenue {
        flex: 1.3;
        min-width: 150px;
    }

    .kpi-revenue-val {
        color: var(--brand-primary, #FF6A00);
        font-weight: 900;
        letter-spacing: -0.3px;
    }

    .text-success { color: var(--semantic-success, #059669) !important; }
    .text-info { color: var(--semantic-info, #2563EB) !important; }
    .text-warning { color: var(--semantic-warning, #D97706) !important; }

    /* TOOLBAR */
    .tables-toolbar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
        gap: 14px;
        flex-wrap: wrap;
    }

    .filter-pills {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
    }

    .filter-pill {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        background: #FFFFFF;
        border: 1px solid var(--neutral-border, #CBD5E1);
        padding: 7px 14px;
        border-radius: var(--radius-md, 10px);
        font-size: 13px;
        font-weight: 600;
        color: var(--text-secondary, #475569);
        cursor: pointer;
        transition: all 0.15s ease;
    }

    .filter-pill:hover {
        background: var(--neutral-surface-hover, #F1F5F9);
        color: var(--text-primary, #0F172A);
        border-color: var(--neutral-border-focus, #94A3B8);
    }

    .filter-pill.active {
        background: var(--brand-primary, #FF6A00);
        color: #FFFFFF;
        border-color: var(--brand-primary, #FF6A00);
        box-shadow: 0 2px 6px rgba(255, 106, 0, 0.25);
    }

    .pill-count {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: rgba(0, 0, 0, 0.07);
        padding: 1px 6px;
        border-radius: 9999px;
        font-size: 11px;
        font-weight: 700;
        margin-left: 2px;
    }

    .filter-pill.active .pill-count {
        background: rgba(255, 255, 255, 0.25);
        color: #FFFFFF;
    }

    .dot-status {
        width: 7px;
        height: 7px;
        border-radius: 50%;
    }

    .dot-status.green { background: var(--semantic-success, #059669); }
    .dot-status.blue { background: var(--semantic-info, #2563EB); }
    .dot-status.orange { background: var(--semantic-warning, #D97706); }

    .toolbar-actions {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .btn-secondary-action {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        background: #FFFFFF;
        color: var(--text-primary, #0F172A);
        border: 1px solid var(--neutral-border, #CBD5E1);
        padding: 8px 14px;
        border-radius: var(--radius-md, 10px);
        font-size: 13px;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.15s ease;
    }

    .btn-secondary-action:hover {
        background: var(--neutral-surface-hover, #F1F5F9);
        border-color: var(--neutral-border-focus, #94A3B8);
    }

    .btn-outline-danger {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        background: var(--semantic-danger-soft, #FEF2F2);
        color: var(--semantic-danger, #DC2626);
        border: 1px solid #FECACA;
        padding: 8px 14px;
        border-radius: var(--radius-md, 10px);
        font-size: 13px;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.15s ease;
    }

    .btn-outline-danger:hover {
        background: var(--semantic-danger, #DC2626);
        color: #FFFFFF;
        border-color: var(--semantic-danger, #DC2626);
    }

    .btn-primary {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        background: var(--brand-primary, #FF6A00);
        color: #FFFFFF;
        border: none;
        padding: 8px 16px;
        border-radius: var(--radius-md, 10px);
        font-size: 13px;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.15s ease;
    }

    .btn-primary:hover {
        background: var(--brand-primary-hover, #E85A00);
        transform: translateY(-1px);
    }

    .btn-primary:active {
        background: var(--brand-primary-active, #CF4F00);
        transform: translateY(0);
    }

    /* RESPONSIVIDADE ESTRUTURAL (TABLES VIEW) */
    @media (max-width: 1023px) {
        .tables-header {
            flex-direction: column;
            align-items: stretch;
            gap: 14px;
        }

        .kpi-bar {
            width: 100%;
            overflow-x: auto;
            flex-wrap: nowrap;
            padding: 10px 16px;
            gap: 14px;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: thin;
        }

        .tables-toolbar {
            flex-wrap: wrap;
            gap: 12px;
        }
    }

    @media (max-width: 767.98px) {
        .tables-view {
            padding: 10px 10px 80px 10px;
        }

        .kpi-bar {
            display: none !important;
        }

        .tables-toolbar {
            margin-bottom: 10px;
            gap: 8px;
            flex-direction: column;
            align-items: stretch;
        }

        .toolbar-actions {
            display: none !important;
        }

        .filter-pills {
            position: relative;
            overflow-x: auto;
            flex-wrap: nowrap;
            padding-bottom: 4px;
            max-width: 100%;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            gap: 6px;
        }

        .filter-pills::-webkit-scrollbar {
            display: none;
        }

        .filter-pill {
            flex-shrink: 0;
            white-space: nowrap;
            min-height: 38px;
            padding: 6px 12px;
            font-size: 12px;
            font-weight: 700;
            border-radius: 999px;
            touch-action: manipulation;
        }
    }
</style>
